import { Card } from "design-system/molecules/Card"
import { Section } from "design-system/molecules/Section"
import { useMetafieldsConfigState } from "modules/settings/hooks/shopify/useMetafieldsConfigState"
import { useTranslation } from "react-i18next"
import { ShopifyMetafieldsConfig } from "schema"
import { MetafieldsAddButton } from "./MetafieldsAddButton"
import { MetafieldEntry, MetafieldGroup, MetafieldRow } from "./MetafieldsKeyList"

const GROUPS: MetafieldGroup[] = [
  "product",
  "productVariant",
  "order",
  "productTags",
  "orderTags",
  "lineItemProperty",
]

const isEmpty = (config: ShopifyMetafieldsConfig) => GROUPS.every((g) => config[g].length === 0)

export const Metafields = () => {
  const { t } = useTranslation()
  const { config, setConfig, addEntry, autosave: onAutosave } = useMetafieldsConfigState()

  const updateEntry = (group: MetafieldGroup, index: number, patch: Partial<MetafieldEntry>) => {
    setConfig((prev) => ({
      ...prev,
      [group]: prev[group].map((e, i) => (i === index ? { ...e, ...patch } : e)),
    }))
  }

  const removeEntry = (group: MetafieldGroup, index: number) => {
    setConfig((prev) => ({ ...prev, [group]: prev[group].filter((_, i) => i !== index) }))
    // removal counts as a change worth persisting
    queueMicrotask(onAutosave)
  }

  if (isEmpty(config)) {
    return (
      <Section
        title={t("settings.integrations.shopify.metafields.title")}
        description={t("settings.integrations.shopify.metafields.description")}
        action={<MetafieldsAddButton onAdd={addEntry} />}
      >
        <Card className="p-4 text-center flex flex-col items-start">
          <h3 className="text-center text-lg font-medium text-foreground">
            {t("settings.integrations.shopify.metafields.noMetafields.title")}
          </h3>
          <p className="text-center text-sm text-muted-foreground">
            {t("settings.integrations.shopify.metafields.noMetafields.description")}
          </p>
        </Card>
      </Section>
    )
  }

  const usedKeysByGroup: Record<MetafieldGroup, Set<string>> = {
    product: new Set(config.product.map((e) => e.key).filter(Boolean)),
    productVariant: new Set(config.productVariant.map((e) => e.key).filter(Boolean)),
    order: new Set(config.order.map((e) => e.key).filter(Boolean)),
    productTags: new Set(config.productTags.map((e) => e.key).filter(Boolean)),
    orderTags: new Set(config.orderTags.map((e) => e.key).filter(Boolean)),
    lineItemProperty: new Set(config.lineItemProperty.map((e) => e.key).filter(Boolean)),
  }

  return (
    <Section
      title={t("settings.integrations.shopify.metafields.title")}
      description={t("settings.integrations.shopify.metafields.description")}
      action={<MetafieldsAddButton onAdd={addEntry} />}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {GROUPS.flatMap((group) =>
          config[group].map((entry, index) => (
            <MetafieldRow
              key={`${group}-${index}`}
              group={group}
              entry={entry}
              usedKeys={usedKeysByGroup[group]}
              onChange={(patch) => updateEntry(group, index, patch)}
              onRemove={() => removeEntry(group, index)}
              onBlur={onAutosave}
            />
          )),
        )}
      </div>
    </Section>
  )
}
